/**
 * The map's event button: a chunky, pressable stone.
 *
 * The stone has a glossy top, a solid shade underneath that reads as depth,
 * and a press animation that collapses that shade. Completed stones wear a
 * gold star badge; the current stone gets a pulsing halo. The face colour is
 * whatever the land needs; highlight and shade are derived from it.
 */
import type {MouseEvent, ReactNode} from 'react';
import styled, {keyframes} from 'styled-components';
import {StarIcon} from './journeyIcons';
import {darken, lighten} from './mapColor';

/** Face colour used for locked stones, whatever the land's palette. */
export const LOCKED_STONE_COLOR = '#c9c2b9';

/** How far the face sits above its shade, in pixels. */
const LIFT_PX = 6;

const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.6; }
  70% { transform: scale(1.4); opacity: 0; }
  100% { transform: scale(1.4); opacity: 0; }
`;

/**
 * The face sits half its edge above the given centre, so the face plus its
 * edge is centred on `y` as a whole.
 */
const Anchor = styled.div<{
  $x: number;
  $y: number;
  $size: number;
  $lift: number;
}>`
  position: absolute;
  left: ${({$x, $size}) => $x - $size / 2}px;
  top: ${({$y, $size, $lift}) => $y - $size / 2 - $lift / 2}px;
  width: ${({$size}) => $size}px;
  height: ${({$size}) => $size}px;
`;

/** Gap between the face and the ring, and the ring's width. */
const RING_GAP_PX = 9;
const RING_WIDTH_PX = 7;

/**
 * The ring sits around the face and its edge together, so the coin
 * reads as centred inside it; the ring box is shifted down by half the lift.
 */
const Ring = styled.svg<{$size: number; $lift: number}>`
  position: absolute;
  left: ${-RING_GAP_PX}px;
  top: ${({$lift}) => -RING_GAP_PX + $lift / 2}px;
  width: ${({$size}) => $size + RING_GAP_PX * 2}px;
  height: ${({$size}) => $size + RING_GAP_PX * 2}px;
  pointer-events: none;
  overflow: visible;
`;

const Halo = styled.span<{$color: string}>`
  position: absolute;
  inset: -7px;
  border-radius: 50%;
  border: 4px solid ${({$color}) => $color};
  animation: ${pulse} 1.7s ease-out infinite;
  pointer-events: none;
`;

/** The height of a matte stone's edge, in pixels. */
const MATTE_LIFT_PX = 3;

const Face = styled.button<{
  $top: string;
  $mid: string;
  $bottom: string;
  $isLocked: boolean;
  $isOptional: boolean;
  $isMatte: boolean;
}>`
  position: absolute;
  inset: 0;
  padding: 0;
  border: none;
  border-radius: 50%;
  color: #fff;
  font: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({$isMatte, $top, $mid}) =>
    $isMatte
      ? $mid
      : `radial-gradient(circle at 38% 26%, ${$top} 0%, ${$mid} 58%, ${$mid} 100%)`};
  box-shadow: ${({$isMatte, $bottom}) =>
    $isMatte
      ? `0 ${MATTE_LIFT_PX}px 0 ${$bottom}, 0 6px 12px rgba(40, 20, 10, 0.12)`
      : `0 ${LIFT_PX}px 0 ${$bottom}, 0 ${LIFT_PX + 6}px 14px rgba(40, 20, 10, 0.2)`};
  cursor: ${({$isLocked}) => ($isLocked ? 'default' : 'pointer')};
  transition:
    transform 0.08s ease-out,
    box-shadow 0.08s ease-out,
    filter 0.15s;

  /* Glossy sheen across the upper face; the matte finish has none. */
  &::before {
    content: '';
    display: ${({$isMatte}) => ($isMatte ? 'none' : 'block')};
    position: absolute;
    left: 18%;
    right: 18%;
    top: 9%;
    height: 32%;
    border-radius: 50%;
    background: linear-gradient(
      rgba(255, 255, 255, 0.26),
      rgba(255, 255, 255, 0.04)
    );
    pointer-events: none;
  }

  /* Optional stones carry a dashed inner ring so they read as side quests. */
  &::after {
    content: '';
    position: absolute;
    inset: 4px;
    border-radius: 50%;
    border: 2px dashed rgba(255, 255, 255, 0.55);
    display: ${({$isOptional}) => ($isOptional ? 'block' : 'none')};
    pointer-events: none;
  }

  svg {
    width: ${({$isMatte}) => ($isMatte ? '46%' : '52%')};
    height: ${({$isMatte}) => ($isMatte ? '46%' : '52%')};
    filter: ${({$isMatte}) =>
      $isMatte ? 'none' : 'drop-shadow(0 2px 0 rgba(0, 0, 0, 0.2))'};
  }

  &:hover:not(:disabled) {
    filter: brightness(1.06);
  }

  &:active:not(:disabled) {
    transform: translateY(
      ${({$isMatte}) => ($isMatte ? MATTE_LIFT_PX : LIFT_PX)}px
    );
    box-shadow:
      0 0 0 ${({$bottom}) => $bottom},
      0 3px 6px rgba(40, 20, 10, 0.18);
  }

  &:focus-visible {
    outline: 3px solid rgba(255, 255, 255, 0.9);
    outline-offset: 3px;
  }

  &:disabled {
    color: rgba(255, 255, 255, 0.92);
  }
`;

const Badge = styled.span<{$size: number}>`
  position: absolute;
  right: -${({$size}) => Math.round($size * 0.12)}px;
  top: -${({$size}) => Math.round($size * 0.12)}px;
  width: ${({$size}) => Math.round($size * 0.42)}px;
  height: ${({$size}) => Math.round($size * 0.42)}px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: radial-gradient(circle at 40% 30%, #ffe082, #f4b63b 70%);
  border: 2.5px solid #fff;
  box-shadow:
    0 2px 0 #b8790c,
    0 3px 6px rgba(0, 0, 0, 0.18);
  pointer-events: none;

  svg {
    width: 62%;
    height: 62%;
    filter: drop-shadow(0 1px 0 rgba(0, 0, 0, 0.18));
  }
`;

/** Props for {@link JourneyStone}. */
export interface JourneyStoneProps {
  /** Centre of the stone on the canvas, in pixels. */
  x: number;
  y: number;
  /** Diameter of the face in pixels. */
  size: number;
  /** Face colour as 6-digit hex. Highlight and shade derive from it. */
  color: string;
  /** The event icon shown on the face. */
  icon: ReactNode;
  /** Wears the gold star badge. Defaults to false. */
  hasStar?: boolean;
  /** Shows the pulsing halo. Defaults to false. */
  isCurrent?: boolean;
  /**
   * When given, a full ring is drawn around the stone to mark it. The ring
   * does not show the value (the share of the week done): a partly filled
   * ring read as progress through the stone itself. When omitted, no ring
   * is drawn.
   */
  progress?: number;
  /** Disables the button and the press animation. Defaults to false. */
  isLocked?: boolean;
  /**
   * Keeps a locked stone pressable, for a viewer who may open locked items.
   * Defaults to false.
   */
  canOpenLocked?: boolean;
  /** Draws the dashed inner ring of an optional item. Defaults to false. */
  isOptional?: boolean;
  /**
   * Surface finish. `glossy` is the candy-like face with a sheen and a deep
   * lift; `matte` is a flat fill with a short edge. Defaults to `glossy`.
   */
  finish?: 'glossy' | 'matte';
  /** Accessible name for the button. */
  label: string;
  /** Native tooltip text. When omitted, none is shown. */
  title?: string;
  /** Pressed state for screen readers. When omitted, not announced. */
  isPressed?: boolean;
  /**
   * Whether the stone's disclosure (a link item's callout) is open. When
   * omitted, the stone is a plain button and `aria-expanded` is not set.
   */
  isExpanded?: boolean;
  /** Id of the element the stone opens, while it is open. */
  controls?: string;
  /** Click handler. When omitted, the stone is decorative and disabled. */
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
}

/** A pressable stone on the trail. */
export function JourneyStone({
  x,
  y,
  size,
  color,
  icon,
  hasStar = false,
  isCurrent = false,
  progress,
  isLocked = false,
  canOpenLocked = false,
  isOptional = false,
  finish = 'glossy',
  label,
  title,
  isPressed,
  isExpanded,
  controls,
  onClick,
}: JourneyStoneProps) {
  const isMatte = finish === 'matte';
  const lift = isMatte ? MATTE_LIFT_PX : LIFT_PX;
  const ringRadius = size / 2 + RING_GAP_PX - RING_WIDTH_PX / 2;
  return (
    <Anchor $x={x} $y={y} $size={size} $lift={lift}>
      {progress !== undefined && (
        <Ring $size={size} $lift={lift} aria-hidden>
          <g
            transform={`translate(${size / 2 + RING_GAP_PX} ${size / 2 + RING_GAP_PX}) rotate(-90)`}
          >
            <circle
              r={ringRadius}
              fill="none"
              stroke={color}
              strokeWidth={RING_WIDTH_PX}
            />
          </g>
        </Ring>
      )}
      {isCurrent && <Halo $color={color} />}
      <Face
        type="button"
        $top={lighten(color, 0.3)}
        $mid={color}
        $bottom={darken(color, isMatte ? 0.3 : 0.38)}
        $isLocked={isLocked && !canOpenLocked}
        $isOptional={isOptional}
        $isMatte={isMatte}
        disabled={(isLocked && !canOpenLocked) || !onClick}
        aria-label={label}
        aria-pressed={isPressed}
        aria-expanded={isExpanded}
        aria-controls={controls}
        title={title}
        onClick={onClick}
      >
        {icon}
      </Face>
      {hasStar && (
        <Badge $size={size}>
          <StarIcon />
        </Badge>
      )}
    </Anchor>
  );
}
